import * as React from "react";
import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useStore } from "@/domain/store";
import { workspacesById } from "@/domain/seed/workspaces";
import { ChevronRight } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { WorkspaceHeader } from "./WorkspaceHeader";
import { MobileMenu } from "./MobileMenu";
import { PendingApprovalScreen } from "./PendingApprovalScreen";
import { useActiveWorkspace, useActiveProfile, useSeed } from "@/domain/hooks";
import { workspaceNavSections } from "./navSections";

function WorkspaceBreadcrumb({ base, programmeName }: { base: string; programmeName: string }) {
  const { projectId, subProjectId } = useParams();
  const seed = useSeed();
  if (!projectId) return null;
  const project = seed.projects.find((p) => p.id === projectId);
  const subProject = subProjectId ? seed.subProjects.find((sp) => sp.id === subProjectId) : undefined;
  return (
    <nav className="flex items-center gap-1 border-b border-hairline bg-card px-4 py-1.5 text-xs text-faint lg:px-6" aria-label="Location">
      <Link to={base} className="hover:text-ink transition-colors">{programmeName}</Link>
      {project && (
        <>
          <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
          {subProject ? (
            <Link to={`${base}/projects/${projectId}`} className="hover:text-ink transition-colors">{project.name}</Link>
          ) : (
            <span className="font-medium text-ink">{project.name}</span>
          )}
        </>
      )}
      {subProject && (
        <>
          <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
          <span className="font-medium text-ink">{subProject.name}</span>
        </>
      )}
    </nav>
  );
}

/** Per-deal workspace layout: branded header + workspace sidebar + content. */
export function WorkspaceShell({
  rightRail,
  children,
}: {
  rightRail?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { workspaceId } = useParams();
  const setWorkspace = useStore((s) => s.setWorkspace);
  const activeId = useStore((s) => s.activeWorkspaceId);

  // Keep the active workspace in sync with the route so the seed + accent match.
  useEffect(() => {
    if (workspaceId && workspacesById[workspaceId] && workspaceId !== activeId) {
      setWorkspace(workspaceId);
    }
  }, [workspaceId, activeId, setWorkspace]);

  const ws = useActiveWorkspace();
  const profile = useActiveProfile();
  const seed = useSeed();
  const isContractor = profile.group === "contractor";
  const base = `/workspace/${workspaceId ?? ws.id}`;

  // Approval gate (spec §9), demo-mode counterpart of the backend login block: a
  // contractor whose organisation hasn't been approved by oversight can't reach the
  // workspace. Keyed on the seed org's pipelineStage (what the oversight approve/reject
  // actions mutate). Non-contractors and active orgs fall through untouched.
  const activeOrg = seed.organisations.find((o) => o.id === profile.organisationId);
  if (isContractor && (activeOrg?.pipelineStage === "awaiting_gate" || activeOrg?.pipelineStage === "suspended")) {
    return (
      <PendingApprovalScreen
        variant={activeOrg.pipelineStage === "suspended" ? "rejected" : "pending"}
        orgName={activeOrg.name}
        oversightName={seed.workspace.oversight.name}
      />
    );
  }

  const sections = workspaceNavSections(base, isContractor);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-bg">
      <WorkspaceHeader />
      <WorkspaceBreadcrumb base={base} programmeName={ws.programmeName} />
      <div className="flex min-h-0 flex-1">
        <div className="hidden lg:flex">
          <Sidebar context={ws.programmeName} sections={sections} showBrand={false} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Mobile nav bar */}
          <div className="flex items-center gap-2 border-b border-line bg-card px-3 py-1.5 lg:hidden">
            <MobileMenu context={ws.programmeName} sections={sections} showBrand={false} />
            <span className="truncate text-sm font-medium text-ink">{ws.programmeName}</span>
          </div>
          <main className="min-w-0 flex-1 overflow-y-auto">{children}</main>
        </div>
        <div className="hidden xl:flex">{rightRail}</div>
      </div>
    </div>
  );
}
